/**
 * The global strip's state (UX spec §2.3, reduced by fix I): one notice at a
 * time, priority registration-failed (F10) > reconnecting.
 *
 * Context-free even though it is outside the Stage: `session.connected` and the
 * auth flag are mirrored into stores by the screen root, and "Reconnect" goes
 * through the `navigateRef`-style module ref rather than the socket context.
 *
 * Fix I (2026-10-06) MOVED the other two faces out: "opened in another tab"
 * and the refused-token face (E6) are both genuinely BLOCKING states the
 * stranded tab cannot play through, so they are now the full-screen
 * `overlay/ClientChanged` card instead of a one-line strip — see that unit
 * for `anotherTab`/`authFailed`. What is left here:
 *
 * F10 (fix F-B, 2026-10-06): the socket is up and the token was accepted, but
 * `hooks/useGameplayHandlers.ts`'s registration watchdog never got an
 * acknowledgement after 3 resends (`Strip/authStatus.ts`'s `registered` /
 * `watchdogExhausted`), with the plain "Reconnecting…" fallback below it.
 *
 * Fix I REVIEW (2026-10-06): the Strip still has to READ `clientChanged` —
 * not to draw a face from it (the card owns that), but to STAY SILENT while
 * the card's own `anotherTab` or `authFailed` face is up. Without this, a
 * demoted tab whose registration watchdog also happened to exhaust showed
 * "Can't reach the game server" dimmed-through behind the card's 50% scrim —
 * two contradictory messages stacked on top of each other. `authFailed` is
 * recomputed here (not read off `overlay/ClientChanged`, which also delays
 * it behind a grace timer) purely as an exclusion for `visible`; the Strip
 * draws no face for it.
 */
import {useLiveGame} from '@/Store/liveGameStore';

import {requestReconnect} from '../rail/navigateRef';
import {useSocketAuth} from './authStatus';

export type StripModel = {
  /** F10: the socket is up and authed, but registration never got an ack */
  registrationFailed: boolean;
  visible: boolean;
  onReconnect: () => void;
};

export function useStripModel(): StripModel {
  const connected = useLiveGame(s => s.session.connected);
  const authComplete = useSocketAuth(s => s.complete);
  const registered = useSocketAuth(s => s.registered);
  const watchdogExhausted = useSocketAuth(s => s.watchdogExhausted);
  const clientChanged = useSocketAuth(s => s.clientChanged);
  const registrationFailed =
    connected && authComplete && !registered && watchdogExhausted;
  /** overlay/ClientChanged owns this face (undelayed exclusion only — see
   * the docblock above) */
  const authFailed = connected && !authComplete && !clientChanged;

  return {
    registrationFailed,
    visible:
      (registrationFailed || !connected) && !clientChanged && !authFailed,
    onReconnect: requestReconnect,
  };
}
